import React, { useState } from 'react';
import {
  buildGmailSearchQuery,
  GMAIL_RULES_PRESETS,
  presetForRules,
  type GmailAttentionRules,
  type GmailRulesPreset,
} from '@zunelo/shared';
import type { UserSettingsPublic, UserSettingsUpdate } from '../../services/clients/types';
import { Button } from '../../ui/common/Button';
import { SettingsConnectionStatus } from './SettingsConnectionStatus';
import { ProviderIcon } from './ProviderIcon';
import { disconnectGmailOAuthFlow, startGmailOAuthFlow } from './gmailSettingsActions';
import { buttonRowStyle, hintStyle, sectionStyle } from './settingsStyles';

export interface SettingsGmailSectionProps {
  inAccordion?: boolean;
  settings: UserSettingsPublic;
  saving: boolean;
  onSavePatch: (
    patch: UserSettingsUpdate,
    successMessage: string,
    after?: () => void,
  ) => Promise<void>;
  onSettingsRefresh?: (data: UserSettingsPublic) => void;
}

const PRESET_LABELS: Record<GmailRulesPreset, string> = {
  recommended: 'Recomendado',
  inbox: 'Bandeja completa',
  starred: 'Solo destacados',
  custom: 'Personalizado',
};

const checkboxRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  color: 'var(--z-text-3)',
};

const numberFieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: 'var(--z-dim)',
};

export const SettingsGmailSection: React.FC<SettingsGmailSectionProps> = ({
  settings,
  saving,
  onSavePatch,
  onSettingsRefresh,
  inAccordion = false,
}) => {
  const gmail = settings.gmail;
  const connected = gmail.connected;
  const [oauthBusy, setOauthBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [rulesDraft, setRulesDraft] = useState<GmailAttentionRules>(gmail.rules);

  const activePreset = presetForRules(rulesDraft);
  const previewQuery = buildGmailSearchQuery(rulesDraft);

  const saveRules = (next: GmailAttentionRules) => {
    setRulesDraft(next);
    void onSavePatch({ gmail: { rules: next } }, 'Reglas de Gmail actualizadas');
  };

  return (
    <section style={{ ...sectionStyle, borderBottom: 'none' }} data-testid="settings-gmail-section">
      <SettingsConnectionStatus
        testIdPrefix="settings-gmail"
        title="Gmail"
        chipLabel={connected ? `Conectado · ${gmail.email || 'cuenta de Google'}` : 'No conectado'}
        chipOk={connected}
        hint="Conecta tu cuenta de Google para traer correos relevantes a tu radar de atención. Solo lectura, marcar como leído y responder."
        icon={<ProviderIcon name="gmail" />}
        compact={inAccordion}
      >
        {localError ? (
          <p
            data-testid="settings-gmail-local-error"
            style={{ ...hintStyle, color: 'var(--z-danger)' }}
          >
            {localError}
          </p>
        ) : null}

        <div style={buttonRowStyle}>
          <Button
            size="sm"
            variant="primary"
            data-testid="settings-gmail-oauth-connect"
            disabled={saving || oauthBusy}
            loading={oauthBusy}
            onClick={() =>
              void startGmailOAuthFlow({ setBusy: setOauthBusy, setError: setLocalError })
            }
          >
            {connected ? 'Reconectar con Google' : 'Conectar con Google (OAuth)'}
          </Button>
          {connected ? (
            <Button
              size="sm"
              variant="bad"
              data-testid="settings-gmail-oauth-disconnect"
              disabled={saving || oauthBusy}
              onClick={() =>
                void disconnectGmailOAuthFlow({
                  setBusy: setOauthBusy,
                  setError: setLocalError,
                  onSettingsRefresh,
                })
              }
            >
              Desconectar Gmail
            </Button>
          ) : null}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--z-text-2)' }}>
            Reglas de atención
          </div>

          <div style={buttonRowStyle}>
            {(Object.keys(GMAIL_RULES_PRESETS) as Array<Exclude<GmailRulesPreset, 'custom'>>).map(
              (preset) => (
                <Button
                  key={preset}
                  size="sm"
                  variant={activePreset === preset ? 'primary' : 'ghost'}
                  data-testid={`settings-gmail-preset-${preset}`}
                  disabled={saving}
                  onClick={() => saveRules(GMAIL_RULES_PRESETS[preset])}
                >
                  {PRESET_LABELS[preset]}
                </Button>
              ),
            )}
          </div>

          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              data-testid="settings-gmail-rule-only-unread"
              checked={rulesDraft.onlyUnread}
              onChange={(e) => saveRules({ ...rulesDraft, onlyUnread: e.target.checked })}
            />
            Solo correos sin leer
          </label>
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              data-testid="settings-gmail-rule-direct-to-me"
              checked={rulesDraft.onlyDirectToMe}
              onChange={(e) => saveRules({ ...rulesDraft, onlyDirectToMe: e.target.checked })}
            />
            Solo donde soy destinatario directo
          </label>
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              data-testid="settings-gmail-rule-starred"
              checked={rulesDraft.includeStarred}
              onChange={(e) => saveRules({ ...rulesDraft, includeStarred: e.target.checked })}
            />
            Incluir destacados
          </label>
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              data-testid="settings-gmail-rule-important"
              checked={rulesDraft.includeImportant}
              onChange={(e) => saveRules({ ...rulesDraft, includeImportant: e.target.checked })}
            />
            Incluir marcados como importantes por Gmail
          </label>
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              data-testid="settings-gmail-rule-exclude-noreply"
              checked={rulesDraft.excludeNoReply}
              onChange={(e) => saveRules({ ...rulesDraft, excludeNoReply: e.target.checked })}
            />
            Excluir remitentes automáticos (no-reply)
          </label>

          <div style={{ display: 'flex', gap: 16 }}>
            <label style={numberFieldStyle}>
              Días hacia atrás
              <input
                type="number"
                min={1}
                max={90}
                data-testid="settings-gmail-lookback-days"
                value={rulesDraft.lookbackDays}
                onChange={(e) =>
                  saveRules({ ...rulesDraft, lookbackDays: Number(e.target.value) || 1 })
                }
                style={{ width: 72 }}
              />
            </label>
            <label style={numberFieldStyle}>
              Máximo de hilos
              <input
                type="number"
                min={5}
                max={50}
                data-testid="settings-gmail-max-threads"
                value={rulesDraft.maxThreads}
                onChange={(e) =>
                  saveRules({ ...rulesDraft, maxThreads: Number(e.target.value) || 5 })
                }
                style={{ width: 72 }}
              />
            </label>
          </div>

          <p style={hintStyle} data-testid="settings-gmail-query-preview">
            Búsqueda equivalente en Gmail: <code>{previewQuery}</code>
          </p>
        </div>
      </SettingsConnectionStatus>
    </section>
  );
};
